import { useMemo } from "react"
import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import { usePoGranularity } from "~/hooks/useCompany"
import { useVariantsByIds } from "~/hooks/useVariants"
import { api, type RouterOutputs } from "~/utils/api"
import { invalidateCapacityContexts } from "~/utils/capacityInvalidation"
import { buildNoosPoBatches, type NoosPoBatch, type NoosSkuMeta } from "~/utils/noosPoUtils"
import { resolveVariantBuyQuantities } from "~/utils/noosBuyFill"
import { resolveVariantVendorId } from "~/utils/vendorUtils"

type SuggestedOrders = RouterOutputs["stockOutTimes"]["getSuggestedOrders"]

// Wire shape of `createBuy`/`addVariantToBuy` batches: every PO is ordered on the buy date.
export function toBuyBatchesInput(batches: NoosPoBatch[], buyDate: Date) {
  return batches.map((batch) => ({
    ...batch,
    orderDate: buyDate,
    collectionIds: batch.collectionIds ?? [],
  }))
}

export function invalidateBuyWrites(
  utils: ReturnType<typeof api.useUtils>,
  { supplyPlanId, buyId }: { supplyPlanId: number; buyId?: number },
) {
  return Promise.all([
    utils.supplyPlan.getBuyMembershipByVariant.invalidate({ supplyPlanId }),
    utils.supplyPlan.getBuysForSupplyPlan.invalidate({ supplyPlanId }),
    buyId !== undefined && utils.supplyPlan.getBuy.invalidate({ buyId }),
    utils.supplyPlan.getDraftBuyOrders.invalidate({ supplyPlanId }),
    utils.capacity.getBuyCapacityContext.invalidate(),
    invalidateCapacityContexts(utils),
    utils.purchaseOrder.getAll.invalidate(),
    utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate(),
    utils.stockOutTimes.getSuggestedOrders.invalidate(),
  ])
}

// Turns a NOOS suggestion into a buy's pre_draft PO groups. Shared by CreateBuyDialog and "Add to
// buy", so a variant added later gets the quantities and grouping it would have had from the start.
export function useNoosBuyPoWriter(variantIds: string[]) {
  const { supplyPlan } = useNoosSupplyPlanDetailContext()
  const noosPoGranularity = usePoGranularity("noos")
  const variants = useVariantsByIds(variantIds)

  const { data: vendorsData } = api.vendor.getAll.useQuery()
  // Per-vendor shipping lead times: POs get a per-warehouse ETA (one batch, own shipping leg each).
  const shippingLeadTimesByVendor = useMemo(() => {
    const out = new Map<string, Record<string, number>>()
    for (const v of vendorsData ?? []) {
      const settings = v.procurementSettings as {
        shippingLeadTimes?: Record<string, number>
      } | null
      if (settings?.shippingLeadTimes) out.set(v.vendorId, settings.shippingLeadTimes)
    }
    return out
  }, [vendorsData])

  const skuMetaMap = useMemo(() => {
    const map = new Map<string, NoosSkuMeta>()
    for (const variant of variants) {
      const vendorId = resolveVariantVendorId(variant)
      const vendorName = variant.vendors.find((v) => v.id === vendorId)?.name ?? vendorId
      const leadTimes = {
        ...variant.settings,
        totalLeadTimeDays: variant.settings.totalLeadTimeDays ?? 30,
      }
      const vendorShippingLeadTimes = shippingLeadTimesByVendor.get(vendorId)
      for (const skuId of variant.skuIds) {
        map.set(skuId, {
          vendorId,
          vendorName,
          variantId: variant.variantId,
          variantName: variant.variantName,
          productId: variant.productId,
          productName: variant.productName,
          leadTimes,
          vendorShippingLeadTimes,
        })
      }
    }
    return map
  }, [variants, shippingLeadTimesByVendor])

  function buildBuyPoBatches({
    buyDate,
    coverUntil,
    suggestedOrders,
    warehouseIds,
  }: {
    buyDate: Date
    coverUntil: Date
    suggestedOrders: SuggestedOrders
    warehouseIds: Set<string>
  }): NoosPoBatch[] {
    return buildNoosPoBatches({
      suggestedOrders: resolveVariantBuyQuantities(
        suggestedOrders,
        (skuId) => skuMetaMap.get(skuId)?.variantId,
      ),
      skuMetaMap,
      warehouseAliasMap: new Map(),
      effectiveWarehouseSet: warehouseIds,
      syncPreorderQuantitiesMap: {},
      preordersMap: undefined,
      demandMap: {},
      // Zero-qty rows are dropped at finalize and ERP push, not here.
      onlySuggestedSkus: false,
      orderPlacementDate: buyDate,
      // Each PO's periodEnd = this + its lead time, so every PO carries the same coverage window.
      nextBuyDate: coverUntil,
      collectionIds: supplyPlan.collectionIds,
      ensureRowPerActiveWarehouse: true,
      granularity: noosPoGranularity,
    })
  }

  return {
    variants,
    shippingLeadTimesByVendor,
    buildBuyPoBatches,
  }
}
